<script setup lang="ts">
import { format, subDays, subHours } from "date-fns";
import { useI18n } from "vue-i18n";
import { Profile } from "../../types/PersologOnlineTypes.ts";
import { onMounted, ref } from "vue";

const props = defineProps({
    groupCharts: {
        type: Array as () => string[],
    },
    profiles: {
        type: Object as () => Profile[],
    },
});

const { t } = useI18n();

const emits = defineEmits(["regenerate", "recalculate", "delete"]);

const languageNamesInEnglish = new Intl.DisplayNames(["en"], { type: "language" });
const enrichedProfiles = ref<Profile[]>();

const highlightRow = (rowData: Profile) => {
    const baseDate = new Date();
    const date = new Date(rowData.started + "Z");
    return [{ "bg-primary-100": subHours(baseDate, 1) > date }, { "bg-primary-300": subDays(baseDate, 1) > date }];
};

async function isGroupChart(product: string) {
    return props.groupCharts?.includes(product) ?? false;
}

onMounted(async () => {
    if (!props.profiles?.length) return;

    const profilePromises = props.profiles.map(async (profile: Profile) => {
        const groupChart = await isGroupChart(profile.product);
        return {
            ...profile,
            groupChart,
        } as Profile;
    });

    enrichedProfiles.value = await Promise.all(profilePromises);
});
</script>

<template>
    <DataTable :value="enrichedProfiles" :row-class="highlightRow">
        <template #header>
            <div class="flex flex-wrap items-center justify-between gap-2">
                <span class="text-xl text-content font-bold">{{ t("pending.profiles.header") }}</span>
            </div>
        </template>
        <Column field="id" :header="t('projects.id')"></Column>
        <Column field="product" :header="t('projects.product')"></Column>
        <Column field="hash" :header="t('projects.questionnaire')"></Column>
        <Column field="language" :header="t('projects.language')">
            <template #body="slotProps">
                {{ languageNamesInEnglish.of(slotProps.data.language) }}
            </template>
        </Column>
        <Column field="started" :header="t('projects.started')">
            <template #body="slotProps">
                {{ format(new Date(slotProps.data.started + "Z"), "dd/MM/yyyy HH:mm") }}
            </template>
        </Column>
        <Column :header="t('projects.actions')">
            <template #body="rowData">
                <Button severity="secondary" icon="pi pi-replay" rounded class="mr-2" size="small" @click="emits('regenerate', rowData.data)" />
                <Button
                    v-if="rowData.data.groupChart === false"
                    severity="secondary"
                    icon="pi pi-calculator"
                    rounded
                    class="mr-2"
                    size="small"
                    @click="emits('recalculate', rowData.data)"
                />
                <Button icon="pi pi-trash" rounded severity="secondary" outlined size="small" @click="emits('delete', rowData.data)" />
            </template>
        </Column>
    </DataTable>
</template>
